/**
 * Resetting a password: a staff account's, or a client's portal login.
 *
 * Typed or generated, with or without a change at the next sign-in. Shared by
 * the staff account dialog and a client's Portal access tab, so the rules — and
 * the one place a generated password is ever visible — are the same wherever it
 * is reached. Each passes the endpoint that resets its kind of login.
 *
 * Every reset ends the sessions the account had. That is the server's doing and
 * is said here up front, because somebody reset while signed in will otherwise
 * find themselves signed out and assume something broke.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound } from 'lucide-react';
import {
  ApiError,
  Alert,
  Button,
  Checkbox,
  Modal,
  PasswordInput,
  api,
  toast,
} from '@connect/web-shared';

interface PasswordResetResult {
  /** Only when the password was generated. A typed one is not echoed back. */
  one_time_password: string | null;
  must_change_password: boolean;
}

export function ResetPasswordDialog({
  open,
  onClose,
  endpoint,
  name,
}: {
  open: boolean;
  onClose: () => void;
  /** The reset endpoint for this login; `null` while there is nothing to reset. */
  endpoint: string | null;
  /** Who is being reset, for the heading. */
  name: string;
}) {
  const queryClient = useQueryClient();
  const [generate, setGenerate] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  // On by default: a password that passed through an administrator's hands is
  // best replaced by one only the account holder knows.
  const [mustChange, setMustChange] = useState(true);
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = useMutation({
    mutationFn: () =>
      api.post<PasswordResetResult>(endpoint ?? '', {
        ...(generate ? {} : { password: newPassword }),
        must_change_password: mustChange,
      }),
    onSuccess: (record) => {
      // The users register and a client's account list both show whether a
      // change is pending.
      void queryClient.invalidateQueries({ queryKey: ['iam'] });
      void queryClient.invalidateQueries({ queryKey: ['crm', 'clients'] });
      if (record.one_time_password) {
        setIssued(record.one_time_password);
        return;
      }
      toast.success(
        'Password set',
        record.must_change_password
          ? 'They will choose their own at their next sign-in.'
          : 'They can sign in with it now.',
      );
      close();
    },
    // No toast on failure: the refusal — usually the password policy — is shown
    // inside the form, next to the field it is about.
  });

  function close() {
    setGenerate(true);
    setNewPassword('');
    setMustChange(true);
    setIssued(null);
    setCopied(false);
    reset.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Reset password"
      description={`${name} will be signed out everywhere.`}
      size="sm"
      // Holds a half-typed password, or a generated one not yet copied; a stray
      // click must not throw either away.
      dismissOnBackdrop={false}
      footer={
        issued ? (
          <Button size="sm" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={reset.isPending}
              disabled={!generate && newPassword.length === 0}
              onClick={() => reset.mutate()}
            >
              Reset password
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <Alert tone="warning" title="New password — shown once">
          <span className="mt-1.5 flex items-center gap-2">
            <KeyRound className="size-3.5 shrink-0" aria-hidden />
            <code className="min-w-0 flex-1 truncate font-mono text-sm">{issued}</code>
            <Button
              variant="secondary"
              size="sm"
              aria-label="Copy the password"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(issued);
                  setCopied(true);
                } catch {
                  toast.error('Could not copy', 'Select it and copy it by hand.');
                }
              }}
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            </Button>
          </span>
          <span className="mt-1.5 block text-2xs">
            {mustChange
              ? 'They sign in with it once, then choose their own. It is not shown again.'
              : 'They keep it until they change it. It is not shown again.'}
          </span>
        </Alert>
      ) : (
        <div className="space-y-4">
          {reset.error ? (
            // The server's reason — usually the password policy — as written.
            <Alert tone="danger" title="Could not reset">
              {reset.error instanceof ApiError
                ? reset.error.message
                : 'Could not reach the server. Try again.'}
            </Alert>
          ) : null}

          <Checkbox
            label="Generate a password"
            hint="A random password, shown once after the reset."
            checked={generate}
            onChange={(event) => setGenerate(event.target.checked)}
          />

          {generate ? null : (
            <PasswordInput
              label="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              autoFocus
              hint="Held to the same password policy as choosing your own."
            />
          )}

          <Checkbox
            label="Require a new password at next sign-in"
            hint={
              mustChange
                ? 'They sign in with this password once, then choose their own.'
                : 'They keep this password until they change it.'
            }
            checked={mustChange}
            onChange={(event) => setMustChange(event.target.checked)}
          />
        </div>
      )}
    </Modal>
  );
}
